import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", address: "", postal_code: "", city: "", country: "Deutschland", tax_id: "" };

const Customers = () => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => getCustomers().then(setList);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditing(c.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("common.required")); return; }
    const payload = { ...form };
    delete payload.id; delete payload.created_at;
    if (editing) { await updateCustomer(editing, payload); toast.success(t("common.save")); }
    else { await createCustomer(payload); toast.success(t("common.create")); }
    setOpen(false); load();
  };

  const confirmDelete = async () => {
    await deleteCustomer(toDelete.id);
    setToDelete(null); load();
    toast.success(t("common.delete"));
  };

  const field = (key, props = {}) => (
    <div className="grid gap-1.5">
      <Label className="text-xs font-medium text-slate-600">{t(`customer.${key}`)}</Label>
      <Input data-testid={`customer-${key}-input`} value={form[key === "postalCode" ? "postal_code" : key === "taxId" ? "tax_id" : key] || ""}
        onChange={set(key === "postalCode" ? "postal_code" : key === "taxId" ? "tax_id" : key)} {...props} />
    </div>
  );

  return (
    <div>
      <PageHeader title={t("customer.title")}>
        <Button data-testid="new-customer-button" onClick={openNew} className="rounded-lg">
          <Plus className="mr-1.5 h-4 w-4" /> {t("customer.new")}
        </Button>
      </PageHeader>

      {list.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">{t("customer.empty")}</Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card data-testid={`customer-card-${c.id}`} className="group p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-display truncate text-lg font-semibold text-slate-900">{c.name}</p>
                    {c.city && <p className="text-xs text-muted-foreground">{c.postal_code} {c.city}</p>}
                  </div>
                  <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)} className="rounded-md p-1.5 text-slate-500 hover:bg-accent hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-customer-${c.id}`} onClick={() => setToDelete(c)} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                  {c.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</p>}
                  {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</p>}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? t("customer.edit") : t("customer.new")}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            {field("name")}
            <div className="grid grid-cols-2 gap-4">{field("email", { type: "email" })}{field("phone")}</div>
            {field("address")}
            <div className="grid grid-cols-2 gap-4">{field("postalCode")}{field("city")}</div>
            <div className="grid grid-cols-2 gap-4">{field("country")}{field("taxId")}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-customer">{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="save-customer-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("common.deleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-customer" className="bg-rose-600 hover:bg-rose-700">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;
